function sanitizeToken(value = "") {
  return String(value).trim().toLowerCase().replace(/_/g, "-");
}

export function normalizeSimulationType(value = "") {
  const normalized = sanitizeToken(value);

  if (normalized === "play") return "plays";
  if (normalized === "on-the-fly") return "on-the-fly";
  if (normalized === "cold-call") return "cold-call";
  if (normalized === "objection") return "objections";

  const allowed = new Set([
    "plays",
    "discovery",
    "pitch",
    "objections",
    "cold-call",
    "on-the-fly",
    "management",
  ]);

  return allowed.has(normalized) ? normalized : null;
}

export function resolveSimulationTypeFromPath(pathname = "") {
  if (typeof pathname !== "string" || !pathname.trim()) {
    return null;
  }

  const parts = pathname.split("/").filter(Boolean);
  const dashboardIndex = parts.indexOf("dashboard");
  if (dashboardIndex >= 0 && parts[dashboardIndex + 1]) {
    return normalizeSimulationType(parts[dashboardIndex + 1]);
  }

  return null;
}

function inferAudioExtension(mimeType = "") {
  const value = String(mimeType || "").toLowerCase();
  if (value.includes("mp4")) return "mp4";
  if (value.includes("ogg")) return "ogg";
  if (value.includes("wav")) return "wav";
  if (value.includes("mpeg") || value.includes("mp3")) return "mp3";
  return "webm";
}

export function isPresignedS3Url(value = "") {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }

  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    const isS3Host =
      host.includes("s3.amazonaws.com") || host.includes(".s3.");
    if (!isS3Host) {
      return false;
    }

    return (
      parsed.searchParams.has("X-Amz-Signature") ||
      parsed.searchParams.has("X-Amz-Credential") ||
      parsed.searchParams.has("AWSAccessKeyId") ||
      parsed.searchParams.has("Signature")
    );
  } catch {
    return false;
  }
}

export function toUnsignedS3Url(value = "") {
  if (typeof value !== "string" || !value.trim()) {
    return value;
  }

  try {
    const parsed = new URL(value);
    parsed.search = "";
    return parsed.toString();
  } catch {
    return value.split("?")[0];
  }
}

const TARGET_REQUEST_TIMEOUT_MS = 15000;
const UPLOAD_REQUEST_TIMEOUT_MS = 120000;
const MAX_UPLOAD_ATTEMPTS = 3;

const RETRYABLE_HTTP_STATUSES = new Set([408, 409, 425, 429, 500, 502, 503, 504]);

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function readResponseText(response) {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

function createUploadId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getBrowserDiagnostics() {
  if (typeof window === "undefined") {
    return null;
  }

  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;

  return {
    online: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
    visibilityState:
      typeof document !== "undefined" ? document.visibilityState : null,
    userAgent: navigator.userAgent || null,
    connection: connection
      ? {
          effectiveType: connection.effectiveType || null,
          downlink: Number.isFinite(connection.downlink)
            ? connection.downlink
            : null,
          rtt: Number.isFinite(connection.rtt) ? connection.rtt : null,
          saveData:
            typeof connection.saveData === "boolean"
              ? connection.saveData
              : null,
        }
      : null,
  };
}

function getErrorDiagnostics(error) {
  return {
    errorName: error?.name || "UnknownError",
    errorMessage: error?.message || "Unknown error",
    status: error?.status || null,
    requestId: error?.requestId || null,
    stack: error?.stack || null,
    causeName: error?.cause?.name || null,
    causeMessage: error?.cause?.message || null,
  };
}

function getTargetDiagnostics(target) {
  return {
    key: target?.key || null,
    generatedAt: target?.generatedAt || null,
    expiresAt: target?.expiresAt || null,
    expiresInSeconds: target?.expiresIn || null,
  };
}

async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } catch (error) {
    if (error?.name === "AbortError") {
      const timeoutError = new Error(
        `Request timed out after ${Math.round(timeoutMs / 1000)} seconds`
      );
      timeoutError.name = "TimeoutError";
      throw timeoutError;
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

function isLikelyRetryableError(error) {
  if (!error) return false;
  if (error.retryable) return true;

  return (
    error.name === "TimeoutError" ||
    error.name === "AbortError" ||
    error instanceof TypeError
  );
}

function responseBodySuggestsExpiredSignature(bodyText = "") {
  const normalizedBody = String(bodyText).toLowerCase();
  return (
    normalizedBody.includes("request has expired") ||
    normalizedBody.includes("requesttimetooskewed") ||
    normalizedBody.includes("signaturedoesnotmatch") ||
    normalizedBody.includes("expiredtoken") ||
    normalizedBody.includes("authorization query parameters error")
  );
}

async function createAudioUploadTarget({ filename, contentType, uploadId }) {
  const targetResponse = await fetchWithTimeout(
    "/api/audio/save-recording",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filename,
        contentType,
        uploadId,
      }),
    },
    TARGET_REQUEST_TIMEOUT_MS
  );

  const payloadText = await readResponseText(targetResponse);
  let targetPayload = null;
  if (payloadText) {
    try {
      targetPayload = JSON.parse(payloadText);
    } catch {
      targetPayload = null;
    }
  }

  if (
    !targetResponse.ok ||
    !targetPayload?.success ||
    !targetPayload?.url ||
    !targetPayload?.baseUrl
  ) {
    const targetError = new Error(
      targetPayload?.error ||
        (payloadText
          ? `Failed to create audio upload target (${targetResponse.status}): ${payloadText.slice(0, 280)}`
          : `Failed to create audio upload target (${targetResponse.status})`)
    );
    targetError.name = "AudioUploadTargetError";
    targetError.status = targetResponse.status;
    targetError.retryable =
      targetResponse.status >= 500 || RETRYABLE_HTTP_STATUSES.has(targetResponse.status);
    throw targetError;
  }

  return targetPayload;
}

async function verifyUploadedObject({
  key,
  expectedSize,
  uploadId,
}) {
  if (!key) {
    return null;
  }

  const response = await fetchWithTimeout(
    "/api/audio/save-recording",
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        key,
        expectedSize,
        uploadId,
      }),
    },
    TARGET_REQUEST_TIMEOUT_MS
  );

  const payloadText = await readResponseText(response);
  let payload = null;
  if (payloadText) {
    try {
      payload = JSON.parse(payloadText);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const verificationError = new Error(
      payload?.error ||
        `Failed to verify audio upload (${response.status})`
    );
    verificationError.name = "AudioUploadVerificationError";
    verificationError.status = response.status;
    throw verificationError;
  }

  return payload;
}

async function uploadAudioToSignedUrl({ uploadUrl, blob, mimeType }) {
  const uploadResponse = await fetchWithTimeout(
    uploadUrl,
    {
      method: "PUT",
      body: blob,
      headers: {
        "Content-Type": mimeType,
      },
    },
    UPLOAD_REQUEST_TIMEOUT_MS
  );

  if (uploadResponse.ok) {
    return;
  }

  const bodyText = await readResponseText(uploadResponse);
  const shouldRefreshSignedUrl =
    uploadResponse.status === 403 ||
    ((uploadResponse.status === 400 || uploadResponse.status === 401) &&
      responseBodySuggestsExpiredSignature(bodyText));

  const uploadError = new Error(
    bodyText
      ? `Failed to upload audio (${uploadResponse.status}): ${bodyText.slice(0, 280)}`
      : `Failed to upload audio (${uploadResponse.status})`
  );
  uploadError.name = "AudioUploadError";
  uploadError.status = uploadResponse.status;
  uploadError.retryable =
    RETRYABLE_HTTP_STATUSES.has(uploadResponse.status) || shouldRefreshSignedUrl;
  uploadError.shouldRefreshSignedUrl = shouldRefreshSignedUrl;
  uploadError.requestId = uploadResponse.headers.get("x-amz-request-id") || null;
  throw uploadError;
}

export async function uploadAudioBlobToS3({
  blob,
  mimeType = "",
  filename = "",
}) {
  if (!(blob instanceof Blob)) {
    throw new Error("Audio blob is required for upload");
  }

  const effectiveMimeType =
    mimeType || blob.type || "audio/webm;codecs=opus";
  const extension = inferAudioExtension(effectiveMimeType);
  const normalizedFilename =
    filename && String(filename).trim()
      ? String(filename).trim()
      : `conversation.${extension}`;
  const uploadId = createUploadId();
  const uploadStartedAt = Date.now();

  let uploadTarget;
  try {
    uploadTarget = await createAudioUploadTarget({
      filename: normalizedFilename,
      contentType: effectiveMimeType,
      uploadId,
    });
  } catch (error) {
    console.error("[AudioUpload] Failed to create upload target", {
      uploadId,
      filename: normalizedFilename,
      mimeType: effectiveMimeType,
      blobSize: blob.size,
      browser: getBrowserDiagnostics(),
      ...getErrorDiagnostics(error),
    });
    throw error;
  }

  console.info("[AudioUpload] Starting upload", {
    uploadId,
    filename: normalizedFilename,
    mimeType: effectiveMimeType,
    blobSize: blob.size,
    browser: getBrowserDiagnostics(),
    ...getTargetDiagnostics(uploadTarget),
  });

  let lastError = null;
  let attemptsMade = 0;

  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
    attemptsMade = attempt;
    const attemptStartedAt = Date.now();

    try {
      await uploadAudioToSignedUrl({
        uploadUrl: uploadTarget.url,
        blob,
        mimeType: effectiveMimeType,
      });

      console.info("[AudioUpload] Upload succeeded", {
        uploadId,
        attempt,
        blobSize: blob.size,
        attemptElapsedMs: Date.now() - attemptStartedAt,
        totalElapsedMs: Date.now() - uploadStartedAt,
        ...getTargetDiagnostics(uploadTarget),
      });
      return uploadTarget.baseUrl;
    } catch (error) {
      lastError = error;
      const retryable = isLikelyRetryableError(error);
      const transportFailure =
        error?.name === "TimeoutError" ||
        error?.name === "AbortError" ||
        error instanceof TypeError;

      if (transportFailure) {
        try {
          const verification = await verifyUploadedObject({
            key: uploadTarget.key,
            expectedSize: blob.size,
            uploadId,
          });

          if (verification?.exists && verification?.sizeMatches) {
            console.warn(
              "[AudioUpload] Client request failed but S3 object was verified",
              {
                uploadId,
                attempt,
                blobSize: blob.size,
                contentLength: verification.contentLength,
                attemptElapsedMs: Date.now() - attemptStartedAt,
                totalElapsedMs: Date.now() - uploadStartedAt,
                browser: getBrowserDiagnostics(),
                ...getTargetDiagnostics(uploadTarget),
                ...getErrorDiagnostics(error),
              }
            );
            return uploadTarget.baseUrl;
          }

          console.warn("[AudioUpload] Object not found after transport failure", {
            uploadId,
            attempt,
            blobSize: blob.size,
            verification,
            browser: getBrowserDiagnostics(),
            ...getTargetDiagnostics(uploadTarget),
            ...getErrorDiagnostics(error),
          });
        } catch (verificationError) {
          console.warn("[AudioUpload] Object verification failed", {
            uploadId,
            attempt,
            key: uploadTarget.key || null,
            ...getErrorDiagnostics(verificationError),
          });
        }
      }

      const willRetry = retryable && attempt < MAX_UPLOAD_ATTEMPTS;
      const retryDelayMs = willRetry
        ? Math.min(1000 * 2 ** (attempt - 1), 4000)
        : null;

      console.warn("[AudioUpload] Upload attempt failed", {
        uploadId,
        attempt,
        maxAttempts: MAX_UPLOAD_ATTEMPTS,
        willRetry,
        retryDelayMs,
        blobSize: blob.size,
        mimeType: effectiveMimeType,
        attemptElapsedMs: Date.now() - attemptStartedAt,
        totalElapsedMs: Date.now() - uploadStartedAt,
        browser: getBrowserDiagnostics(),
        ...getTargetDiagnostics(uploadTarget),
        ...getErrorDiagnostics(error),
      });

      if (!willRetry) {
        break;
      }

      if (error?.shouldRefreshSignedUrl) {
        try {
          uploadTarget = await createAudioUploadTarget({
            filename: normalizedFilename,
            contentType: effectiveMimeType,
            uploadId,
          });
        } catch (targetError) {
          lastError = targetError;
          console.warn("[AudioUpload] Failed to refresh upload target", {
            uploadId,
            attempt,
            browser: getBrowserDiagnostics(),
            ...getErrorDiagnostics(targetError),
          });
          if (!isLikelyRetryableError(targetError)) {
            break;
          }
        }
      }

      await sleep(retryDelayMs);
    }
  }

  const finalError = new Error(
    `Failed to upload audio after ${attemptsMade} attempt${
      attemptsMade === 1 ? "" : "s"
    }${
      lastError?.message ? `: ${lastError.message}` : ""
    }`
  );
  finalError.name = "AudioUploadFailedError";
  finalError.status = lastError?.status || null;
  finalError.attempts = attemptsMade;
  finalError.uploadId = uploadId;
  finalError.key = uploadTarget?.key || null;
  finalError.elapsedMs = Date.now() - uploadStartedAt;
  finalError.cause = lastError || null;
  throw finalError;
}
